import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Plus, 
  Sparkles, 
  ArrowUpRight, 
  BarChart2, 
  Calendar as CalendarIcon, 
  Users, 
  MessageSquare, 
  ChevronRight, 
  Edit, 
  ThumbsUp, 
  Eye, 
  Share2, 
  Lightbulb, 
  BarChart, 
  Clock,
  Cloud,
  Send,
  TrendingUp,
  Zap,
  Rocket,
  Award,
  Target
} from 'lucide-react';
import { format } from 'date-fns';
import type { WidgetProps } from '../../../types/dashboard';

const trendingTopics = [
  'AI in Marketing',
  'Content Strategy',
  'Social Media Trends'
];

const scheduledPosts = [
  {
    title: 'Marketing Strategy 2025',
    time: '2:00 PM',
    platform: 'LinkedIn'
  },
  {
    title: 'Product Launch Update',
    time: '4:30 PM',
    platform: 'Twitter'
  }
];

const contentTips = [
  "Try using storytelling techniques in your next post to boost engagement. Personal narratives can increase engagement by up to 22%.",
  "The best times to post on social media are typically between 10 AM and 3 PM on weekdays.",
  "Including a call-to-action in your posts can increase engagement rates by up to 35%.",
  "Visual content gets 94% more views than text-only content. Try adding images or videos to your posts.",
  "Engaging with your audience within the first hour of posting can boost your content's visibility.",
  "Using relevant hashtags can increase your post's reach by up to 40%."
];

export const WelcomeWidget: React.FC<WidgetProps> = () => {
  const [message, setMessage] = useState('');
  const [currentTipIndex, setCurrentTipIndex] = useState(0);
  const [greeting, setGreeting] = useState('Welcome back');
  const userName = JSON.parse(localStorage.getItem('sb-bqpssdvpdwxpsjhkdjoc-auth-token') || '{}')
    ?.user?.user_metadata?.full_name || 'User';
  
  // Extract first name for more personal greeting
  const firstName = userName.split(' ')[0];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTipIndex((prevIndex) => (prevIndex + 1) % contentTips.length);
    }, 10000);

    return () => clearInterval(interval);
  }, []);

  // Set greeting based on time of day
  useEffect(() => {
    const setGreetingByTime = () => {
      const currentHour = new Date().getHours();
      
      if (currentHour >= 5 && currentHour < 12) {
        setGreeting('Good Morning');
      } else if (currentHour >= 12 && currentHour < 18) {
        setGreeting('Good Afternoon');
      } else {
        setGreeting('Good Evening');
      }
    };

    // Set initial greeting
    setGreetingByTime();
    
    // Update greeting every minute (in case user is active during time change)
    const intervalId = setInterval(setGreetingByTime, 60000);
    
    return () => clearInterval(intervalId);
  }, []);

  return (
    <div className="h-full flex flex-col">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 flex-1">
        {/* Left Column */}
        <div className="md:col-span-2 space-y-4">
          {/* Welcome Header - Enhanced with animation and depth */}
          <div className="relative overflow-hidden rounded-xl bg-gradient-to-r from-indigo-600 via-indigo-700 to-purple-700 p-4 sm:p-6 shadow-lg">
            {/* Animated background elements */}
            <div className="absolute top-0 right-0 w-64 h-64 transform translate-x-32 -translate-y-32 animate-pulse-slow">
              <div className="absolute inset-0 bg-white opacity-10 blur-3xl rounded-full"></div>
            </div>
            <div className="absolute bottom-0 left-0 w-32 h-32 transform -translate-x-16 translate-y-16 animate-float">
              <div className="absolute inset-0 bg-white opacity-5 blur-2xl rounded-full"></div>
            </div>
            <div className="absolute top-1/2 left-1/4 w-16 h-16 bg-white opacity-10 rounded-full blur-xl animate-pulse"></div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="relative z-10 flex items-start space-x-3 sm:space-x-5"
            >
              <motion.div 
                className="p-2 sm:p-3 bg-white/20 backdrop-blur-sm rounded-lg"
                whileHover={{ rotate: 15, scale: 1.1 }}
                transition={{ type: "spring", stiffness: 300, damping: 10 }}
              >
                <Cloud className="h-5 w-5 sm:h-6 sm:w-6 text-white drop-shadow-lg" />
              </motion.div>
              <div>
                <motion.h3 
                  className="text-xl sm:text-2xl font-bold text-white drop-shadow-md"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                >
                  {greeting}, {firstName}!
                </motion.h3>
                <motion.p 
                  className="text-white/90 text-xs sm:text-sm mt-1 sm:mt-2"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.2 }}
                >
                  {format(new Date(), 'EEEE, MMMM do')}
                </motion.p>
                <motion.div 
                  className="mt-2 sm:mt-4 flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-3"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  <motion.span 
                    className="inline-flex items-center px-2 sm:px-3 py-1 rounded-full text-xs font-medium bg-white/20 backdrop-blur-sm text-white"
                    whileHover={{ scale: 1.05 }}
                  >
                    <Clock className="mr-1.5 h-3 w-3" />
                    Last login: {format(new Date(), 'h:mm a')}
                  </motion.span>
                  <motion.span 
                    className="inline-flex items-center px-2 sm:px-3 py-1 rounded-full text-xs font-medium bg-white/20 backdrop-blur-sm text-white"
                    whileHover={{ scale: 1.05 }}
                  >
                    <Zap className="mr-1.5 h-3 w-3" />
                    2 new insights
                  </motion.span>
                </motion.div>
              </div>
            </motion.div>
          </div>

          {/* AI Assistant and Content Tips */}
          <div className="grid grid-cols-1 gap-3 sm:gap-4">
            {/* Content Tip */}
            <AnimatePresence mode="wait">
              <motion.div
                key={currentTipIndex}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="bg-gradient-to-r from-amber-50 to-yellow-50 dark:from-amber-900/20 dark:to-yellow-900/20 rounded-xl p-3 sm:p-5 border border-amber-100 dark:border-amber-800/30"
              >
                <div className="flex items-start space-x-3 sm:space-x-4">
                  <Lightbulb className="h-4 w-4 sm:h-5 sm:w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h4 className="font-medium text-amber-900 dark:text-amber-100 text-xs sm:text-sm">Content Tip</h4>
                    <p className="text-xs sm:text-sm text-amber-800 dark:text-amber-200 mt-1 sm:mt-2">{contentTips[currentTipIndex]}</p>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
            
            {/* AI Assistant Input */}
            <motion.div 
              className="relative"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              whileHover={{ scale: 1.02 }}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-indigo-500/10 to-purple-500/10 rounded-xl"></div>
              <div className="absolute inset-0 bg-white/50 dark:bg-dark-800/50 backdrop-blur-sm rounded-xl"></div>
              <div className="relative p-2 flex items-center">
                <motion.div 
                  className="w-full"
                  whileHover={{ boxShadow: "0 0 15px 5px rgba(99, 102, 241, 0.15)" }}
                >
                  <input
                    type="text"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Ask me anything about content creation..."
                    className="w-full px-3 sm:px-4 py-2.5 sm:py-3.5 bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-700 rounded-lg text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 transition-all text-xs sm:text-sm shadow-sm"
                  />
                  <motion.div
                    whileHover={{ scale: 1.1, rotate: 15 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <Link 
                      to="/creator-assistant"
                      className="absolute right-3 sm:right-4 top-1/2 -translate-y-1/2 p-1.5 sm:p-2 bg-indigo-100 dark:bg-indigo-900/30 hover:bg-indigo-200 dark:hover:bg-indigo-800/50 rounded-full transition-colors"
                    >
                      <Send className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-indigo-600 dark:text-indigo-400" />
                    </Link>
                  </motion.div>
                </motion.div>
              </div>
            </motion.div>
          </div>

          {/* Activity and Schedule Container */}
          <div className="flex flex-col">
            {/* Today's Schedule */}
            <motion.div 
              className="bg-white dark:bg-dark-800 rounded-lg p-3 sm:p-5 shadow-md border border-gray-100 dark:border-dark-700"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              whileHover={{ y: -5 }}
            >
              <div className="flex items-center justify-between mb-3 sm:mb-5">
                <div className="flex items-center space-x-2">
                  <div className="p-1.5 sm:p-2 bg-indigo-50 dark:bg-indigo-900/20 rounded-md">
                    <CalendarIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-indigo-600 dark:text-indigo-400" />
                  </div>
                  <h4 className="font-medium text-gray-900 dark:text-white text-xs sm:text-sm">Today's Schedule</h4>
                </div>
                <Link to="/calendar" className="text-xs text-indigo-600 dark:text-indigo-400 flex items-center hover:underline">
                  View Calendar <ArrowUpRight className="ml-1 h-3 w-3" />
                </Link>
              </div>
              <div className="space-y-2 sm:space-y-3">
                {scheduledPosts.map((post, index) => (
                  <motion.div
                    key={index}
                    className="flex items-center justify-between text-xs sm:text-sm p-2 sm:p-3 bg-gray-50 dark:bg-dark-700 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-600 transition-all duration-200 border border-transparent hover:border-indigo-100 dark:hover:border-indigo-900/30"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.6 + index * 0.1 }}
                    whileHover={{ x: 5 }}
                  >
                    <div className="flex items-center space-x-3">
                      <div className="w-1.5 h-8 bg-gradient-to-b from-indigo-400 to-indigo-600 rounded-full shadow-sm"></div>
                      <span className="text-gray-900 dark:text-white truncate max-w-[120px] sm:max-w-none">{post.title}</span>
                    </div>
                    <div className="flex flex-col sm:flex-row items-end sm:items-center space-y-1 sm:space-y-0 sm:space-x-2 text-gray-500 shrink-0">
                      <span>{post.time}</span>
                      <span className="text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 bg-indigo-100 dark:bg-indigo-900/50 text-indigo-600 dark:text-indigo-300 rounded-full font-medium">
                        {post.platform}
                      </span>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>

        {/* Right Column - Quick Actions */}
        <div className="space-y-4 sm:space-y-5">
          {/* Quick Actions */}
          <motion.div 
            className="bg-white dark:bg-dark-800 rounded-xl p-3 sm:p-5 shadow-md border border-gray-100 dark:border-dark-700"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
            whileHover={{ y: -5 }}
          >
            <div className="flex items-center justify-between mb-3 sm:mb-4">
              <h4 className="font-medium text-gray-900 dark:text-white text-xs sm:text-sm flex items-center">
                <Rocket className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1.5 sm:mr-2 text-indigo-600 dark:text-indigo-400" />
                Quick Actions
              </h4>
              <button className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline">Customize</button>
            </div>
            <div className="grid grid-cols-1 gap-2 sm:gap-3 group">
              <motion.div
                whileHover={{ 
                  scale: 1.03, 
                  boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)" 
                }}
                className="group-hover:opacity-75 hover:!opacity-100 transition-all duration-300"
              >
                <Link
                to="/library"
                className="flex items-center p-2 sm:p-3 bg-gradient-to-r from-blue-100 to-indigo-100 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-lg group hover:shadow-md transition-all duration-200 border border-blue-200/50 dark:border-blue-800/30"
              >
                <div className="p-2 sm:p-2.5 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-lg shadow-md">
                  <Edit className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-white" />
                </div>
                <div className="ml-3">
                  <p className="text-xs sm:text-sm font-bold text-blue-900 dark:text-blue-100">New Post</p>
                  <p className="text-[10px] sm:text-xs text-blue-600 dark:text-blue-300 opacity-0 group-hover:opacity-100 transition-opacity">Create content →</p>
                </div>
              </Link>
              </motion.div>
                
              <motion.div
                whileHover={{ 
                  scale: 1.03, 
                  boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)" 
                }}
                className="group-hover:opacity-75 hover:!opacity-100 transition-all duration-300"
              >
                <Link
                to="/performance"
                className="flex items-center p-2 sm:p-3 bg-gradient-to-r from-purple-100 to-pink-100 dark:from-purple-900/30 dark:to-pink-900/30 rounded-lg group hover:shadow-md transition-all duration-200 border border-purple-200/50 dark:border-purple-800/30"
              >
                <div className="p-2 sm:p-2.5 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg shadow-md">
                  <BarChart2 className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-white" />
                </div>
                <div className="ml-3">
                  <p className="text-xs sm:text-sm font-bold text-purple-900 dark:text-purple-100">Analytics</p>
                  <p className="text-[10px] sm:text-xs text-purple-600 dark:text-purple-300 opacity-0 group-hover:opacity-100 transition-opacity">View insights →</p>
                </div>
              </Link>
              </motion.div>
                
              <motion.div
                whileHover={{ 
                  scale: 1.03, 
                  boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)" 
                }}
                className="group-hover:opacity-75 hover:!opacity-100 transition-all duration-300"
              >
                <Link
                to="/calendar"
                className="flex items-center p-2 sm:p-3 bg-gradient-to-r from-green-100 to-emerald-100 dark:from-green-900/30 dark:to-emerald-900/30 rounded-lg group hover:shadow-md transition-all duration-200 border border-green-200/50 dark:border-green-800/30"
              >
                <div className="p-2 sm:p-2.5 bg-gradient-to-br from-green-500 to-emerald-500 rounded-lg shadow-md">
                  <CalendarIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-white" />
                </div>
                <div className="ml-3">
                  <p className="text-xs sm:text-sm font-bold text-green-900 dark:text-green-100">Schedule</p>
                  <p className="text-[10px] sm:text-xs text-green-600 dark:text-green-300 opacity-0 group-hover:opacity-100 transition-opacity">Plan content →</p>
                </div>
              </Link>
              </motion.div>
                
              <motion.div
                whileHover={{ 
                  scale: 1.03, 
                  boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)" 
                }}
                className="group-hover:opacity-75 hover:!opacity-100 transition-all duration-300"
              >
                <Link
                to="/contacts"
                className="flex items-center p-2 sm:p-3 bg-gradient-to-r from-amber-100 to-orange-100 dark:from-amber-900/30 dark:to-orange-900/30 rounded-lg group hover:shadow-md transition-all duration-200 border border-amber-200/50 dark:border-amber-800/30"
              >
                <div className="p-2 sm:p-2.5 bg-gradient-to-br from-amber-500 to-orange-500 rounded-lg shadow-md">
                  <Users className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-white" />
                </div>
                <div className="ml-3">
                  <p className="text-xs sm:text-sm font-bold text-amber-900 dark:text-amber-100">Contacts</p>
                  <p className="text-[10px] sm:text-xs text-amber-600 dark:text-amber-300 opacity-0 group-hover:opacity-100 transition-opacity">Manage network →</p>
                </div>
              </Link>
              </motion.div>
            </div>
          </motion.div>

          {/* Recent Activity */}
          <motion.div 
            className="bg-white dark:bg-dark-800 rounded-xl p-3 sm:p-5 shadow-md border border-gray-100 dark:border-dark-700"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.5 }}
            whileHover={{ y: -5 }}
          >
            <div className="flex items-center justify-between mb-3 sm:mb-5">
              <div className="flex items-center space-x-2">
                <div className="p-1.5 sm:p-2 bg-indigo-50 dark:bg-indigo-900/20 rounded-md">
                  <TrendingUp className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-indigo-600 dark:text-indigo-400" />
                </div>
                <h4 className="font-medium text-gray-900 dark:text-white text-xs sm:text-sm">Recent Activity</h4>
              </div>
              <button className="text-xs text-indigo-600 dark:text-indigo-400 flex items-center hover:underline">
                View all <ArrowUpRight className="ml-1 h-3 w-3" />
              </button>
            </div>
            <div className="space-y-2 sm:space-y-3 group">
              <div className="flex items-start space-x-2 sm:space-x-3 p-2 sm:p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors">
                <motion.div 
                  className="p-1.5 sm:p-2 bg-purple-50 dark:bg-purple-900/20 rounded-md"
                  whileHover={{ rotate: 15, scale: 1.1 }}
                >
                  <Users className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-purple-600 dark:text-purple-400" />
                </motion.div>
                <div>
                  <p className="text-xs sm:text-sm font-medium text-gray-800 dark:text-gray-200">Gained 50 new followers</p>
                  <p className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 mt-1 sm:mt-1.5">4 hours ago</p>
                </div>
              </div>
              <div className="flex items-start space-x-2 sm:space-x-3 p-2 sm:p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors">
                <motion.div 
                  className="p-1.5 sm:p-2 bg-blue-50 dark:bg-blue-900/20 rounded-md"
                  whileHover={{ rotate: 15, scale: 1.1 }}
                >
                  <CalendarIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-blue-600 dark:text-blue-400" />
                </motion.div>
                <div>
                  <p className="text-xs sm:text-sm font-medium text-gray-800 dark:text-gray-200">New post scheduled for tomorrow</p>
                  <p className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 mt-1 sm:mt-1.5">2 hours ago</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

